/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/RecordingManagementColumns.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Column definitions for Recording Management DataGrid.
────────────────────────────────────────────────────────────────*/

import { useMemo } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { DataGridColumn } from '../../../hooks/useDataGrid';

/**
 * Generate column definitions for Recording Management
 */
export function useRecordingManagementColumns(): DataGridColumn[] {
  const { t, i18n, refreshKey } = useI18n();

  return useMemo(
    () => [
      {
        key: 'name',
        label: t('recordingManagement.fields.name.label'),
        visible: true,
        sortable: true,
      },

      {
        key: 'duration',
        label: t('recordingManagement.fields.duration.label'),
        visible: true,
        sortable: true,
        render: (value: number) => (
          <span className="text-sm text-muted-foreground">{value ? `${value}s` : '-'}</span>
        ),
      },
      {
        key: 'format',
        label: t('recordingManagement.fields.format.label'),
        visible: true,
        sortable: true,
        render: (value: string) => (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
            {value.toUpperCase()}
          </span>
        ),
      },
      {
        key: 'is_ai_generated',
        label: t('recordingManagement.fields.isAiGenerated.label'),
        visible: true,
        sortable: true,
        render: (value: boolean, row: any) => {
          const isSystem = row?.file_path?.includes('/sounds/');
          
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                value
                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                  : isSystem 
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
              }`}
            >
              {value 
                ? t('recordingManagement.aiGenerated') 
                : isSystem
                  ? t('recordingManagement:modes.system') || 'System'
                  : t('recordingManagement.uploaded')}
            </span>
          );
        },
      },

      {
        key: 'created_at',
        label: t('common:created'),
        visible: false,
        sortable: true,
        render: (value: string) => (
          <div className="text-sm text-muted-foreground">
            {new Date(value).toLocaleDateString()}
          </div>
        ),
      },
      {
        key: 'actions',
        label: t('common:actionsTitle') || t('common:actions'),
        visible: true,
        sortable: false,
      },
    ],
    [t, i18n.language, refreshKey],
  );
}

